import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, IndianRupee, Percent, Crown, FileText, ScrollText } from "lucide-react";

// Fields grouped by domain — each tab is a card.
const GROUPS = [
  {
    id: "pricing", label: "Pricing", icon: IndianRupee,
    fields: [
      { key: "price_bw", label: "B&W per page (₹)", step: 0.5 },
      { key: "price_color", label: "Color per page (₹)", step: 0.5 },
      { key: "price_duplex_extra", label: "Duplex extra / page (₹)", step: 0.5 },
      { key: "price_id_card", label: "ID Card price (₹)", step: 1 },
      { key: "price_passport_sheet", label: "Passport sheet (₹)", step: 1 },
    ],
  },
  {
    id: "paper", label: "Paper multipliers", icon: FileText,
    fields: [
      { key: "paper_a5_multiplier", label: "A5 multiplier", step: 0.1 },
      { key: "paper_letter_multiplier", label: "Letter multiplier", step: 0.1 },
      { key: "paper_legal_multiplier", label: "Legal multiplier", step: 0.1 },
      { key: "paper_a3_multiplier", label: "A3 multiplier", step: 0.1 },
    ],
  },
  {
    id: "commission", label: "Commission & GST", icon: Percent,
    fields: [
      { key: "gst_pct", label: "GST %", step: 0.5 },
      { key: "platform_commission_pct", label: "Platform commission % (walk-in)", step: 0.5 },
      { key: "retailer_commission_pct", label: "Retailer commission %", step: 1 },
      { key: "distributor_commission_pct", label: "Distributor commission %", step: 1 },
    ],
  },
  {
    id: "subscription", label: "Subscriptions", icon: Crown,
    fields: [
      { key: "subscription_basic_inr", label: "Basic plan (₹ / 30 days)", step: 50 },
      { key: "subscription_pro_inr", label: "Pro plan (₹ / 30 days)", step: 50 },
      { key: "subscription_grace_days", label: "Grace period (days)", step: 1 },
      { key: "wallet_low_threshold", label: "Wallet low alert (₹)", step: 10 },
      { key: "file_retention_hours", label: "Customer file retention (hours after print)", step: 1 },
    ],
  },
];

export default function Settings() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/settings");
      setS(data);
    })();
  }, []);

  const upd = (k) => (e) => setS((prev) => ({ ...prev, [k]: parseFloat(e.target.value) || 0 }));

  const save = async () => {
    setLoading(true);
    try {
      const payload = GROUPS.flatMap((g) => g.fields).reduce(
        (acc, f) => ({ ...acc, [f.key]: s[f.key] }), {}
      );
      const { data } = await api.put("/settings", payload);
      setS(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!s) return null;

  return (
    <div className="max-w-4xl space-y-6" data-testid="settings-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Configuration</p>
        <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">Platform settings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Global pricing, taxes, commission rates and subscription tiers. Applies platform-wide instantly.
        </p>
      </div>

      <Tabs defaultValue="pricing">
        <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full">
          {GROUPS.map((g) => {
            const Icon = g.icon;
            return (
              <TabsTrigger key={g.id} value={g.id} data-testid={`settings-tab-${g.id}`} className="gap-1.5">
                <Icon size={13} /> <span className="hidden sm:inline">{g.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {GROUPS.map((g) => (
          <TabsContent value={g.id} key={g.id} className="mt-4">
            <Card className="p-6 border-border bg-card">
              <div className="flex items-center gap-2 mb-4">
                <g.icon className="text-primary" size={18} />
                <h3 className="font-display font-semibold">{g.label}</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {g.fields.map((f) => (
                  <div key={f.key}>
                    <Label>{f.label}</Label>
                    <Input
                      type="number"
                      step={f.step}
                      value={s[f.key] ?? 0}
                      onChange={upd(f.key)}
                      className="mt-1.5 tabular-nums"
                      data-testid={`settings-${f.key}`}
                    />
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      <Card className="p-4 border-border bg-card flex items-center gap-3">
        <ScrollText size={16} className="text-muted-foreground" />
        <p className="text-xs text-muted-foreground flex-1">
          Changes affect the walk-in customer pricing on every shop QR page immediately.
        </p>
        <Button onClick={save} disabled={loading} className="bg-primary hover:bg-brand-blue-hover" data-testid="settings-save-btn">
          <Save size={16} className="mr-2" /> {loading ? "Saving…" : "Save all"}
        </Button>
      </Card>
    </div>
  );
}
